import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import { typedLineMutesAgentAudio } from '../../lib/live-typed-output-mute.js'
import { ROUTE_COLOR } from '../../components/chat/design-tokens.js'
import enChat from '../../i18n/locales/en/chat.json' with { type: 'json' }
import nbChat from '../../i18n/locales/nb/chat.json' with { type: 'json' }
import { useToast } from '../../context/ToastContext.js'
import { GEMINI_MODELS, VOICE_CONFIG, MEDIA_CAPTURE_CONFIG } from '../../config/constants.js'
import {
  CONTEXT_FETCH_TIMEOUT_MS,
  FIRST_CONSULTING_SPOKEN_STORAGE_KEY,
  FIRST_CONSULTING_WATCH_MS,
  LIVE_LINE_UNCONFIRMED,
  LIVE_USER_ASR_ENDPOINT_PAUSE_MS,
  uiLocaleToLanguageCode,
} from '../../core/contracts.js'
import { resolveUiLocale } from '../../i18n/resolve-ui-locale.js'
import { rememberSeenAssistantReply } from '../../lib/remember-seen-reply.js'
import { clientSessionViewStore as uiPreferenceStore } from '../../../services/clientSessionViewStore.js'
import { composeLiveTypedOutbound } from '../../core/agents/utils/consulting-move.js'
import {
  formatHypothesisCorrectionBind,
  isHypothesisCorrection,
  stripHypothesisInstructionLeak,
} from '../../core/agents/utils/working-hypothesis.js'
import {
  isCannedBottleneckWelcome,
  isGenericLiveImprovWelcome,
  normalizeGreetingCompareText,
  readLabeledGuessGreeting,
  readLatestGreeting,
  shouldSpeakFirstConsultingLine,
  transcriptHasVisitorTurn,
} from '../../core/prompts/greetings.js'
import type { SessionProjectionEnvelopeV1 } from '../../core/session/session-projection.js'
import { detectAndAnalyzeUrlsFromServer } from '../../lib/services/url-analysis-client.js'
import { generateTranscriptId } from '../../lib/transcript-append.js'
import {
  isLeakOpenerFragment,
  isGrowingUserAsrRevision,
  isClosedModelOutputReplay,
  isShortModelStub,
  looksLikeCompletedUtterance,
  mergeLiveTranscriptSnapshot,
  isInjectedSayPrompt,
} from '../../lib/live-transcript-reconciler.js'
import { acceptLiveSessionFactsEnvelope } from '../../lib/accept-session-projection-response.js'
import { logger } from '../../lib/logger-client.js'
import { isDiscoveryReportRequest } from '../../core/agents/consultation-intent-router.js'
import {
  liveRuntimeTrace,
  redactTraceText,
  sessionIdPrefix,
} from '../../lib/live-runtime-trace.js'
import { LiveConnectionState, type TranscriptItem, type VisualState } from '../../types/visual.js'
import type { ModelRoute } from '../../logic/smartRouting.js'
import type { LiveSessionPolicy } from '../../types/live-policy.js'
import type { VoiceContextUpdate } from '../../types/voice.js'
import type {
  LiveContextUpdate,
  LiveMediaFrame,
  LiveSessionEvent,
  LiveToolCall,
} from '../../live/liveTypes.js'
import type { LiveRuntimeController } from '../../live/liveRuntimeController.js'
import { useLiveSession } from './useLiveSession.js'

const FBC_LIVE_CONNECTION_STORAGE_KEY = 'fbc_live_connection_state'
const LIVE_READY_TIMEOUT_MS = 5000
const STORED_CONNECTION_STATES = new Set<string>(Object.values(LiveConnectionState))
const URL_REGEX = /(https?:\/\/[^\s]+)/g

async function detectAndAnalyzeUrlsWithBudget(
  messageContent: string
): Promise<string> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  try {
    return await Promise.race([
      detectAndAnalyzeUrlsFromServer(messageContent),
      new Promise<string>(resolve => {
        timeoutId = setTimeout(() => resolve(''), CONTEXT_FETCH_TIMEOUT_MS)
      }),
    ])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

function createReadyRoute(description = 'System ready.'): ModelRoute {
  return {
    id: GEMINI_MODELS.DEFAULT_FAST,
    label: 'READY',
    description,
    color: ROUTE_COLOR.ready,
  }
}

export function normalizeStoredConnectionState(
  value: string | null | undefined
): LiveConnectionState {
  if (!value || !STORED_CONNECTION_STATES.has(value)) {
    return LiveConnectionState.DISCONNECTED
  }

  // Browser reloads and fresh sessions always sever the WebSocket. Do not
  // resurrect transient or failed states as live UI status.
  return LiveConnectionState.DISCONNECTED
}

export function shouldActivateAudioInputForConnect(
  connectionState: LiveConnectionState,
  audioRequested: boolean,
  isAudioInputActive: boolean
): boolean {
  return (
    audioRequested &&
    !isAudioInputActive &&
    (connectionState === LiveConnectionState.CONNECTED ||
      connectionState === LiveConnectionState.CONNECTING)
  )
}

function readStoredFirstConsultingLine(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    return sessionStorage.getItem(FIRST_CONSULTING_SPOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

function persistFirstConsultingLine(line: string): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(FIRST_CONSULTING_SPOKEN_STORAGE_KEY, line)
  } catch {
    /* ignore */
  }
}

function getStoredConnectionState(): LiveConnectionState {
  if (typeof sessionStorage === 'undefined') return LiveConnectionState.DISCONNECTED

  try {
    const stored = sessionStorage.getItem(FBC_LIVE_CONNECTION_STORAGE_KEY)
    return normalizeStoredConnectionState(stored)
  } catch {
    /* ignore */
  }

  return LiveConnectionState.DISCONNECTED
}

function mapState(state: 'disconnected' | 'connecting' | 'connected' | 'error' | 'reconnecting') {
  switch (state) {
    case 'connected':
      return LiveConnectionState.CONNECTED
    case 'connecting':
    case 'reconnecting':
      return LiveConnectionState.CONNECTING
    case 'error':
      return LiveConnectionState.ERROR
    default:
      return LiveConnectionState.DISCONNECTED
  }
}

function isAudioMimeType(mimeType: string) {
  return (
    mimeType.startsWith('audio/') || mimeType.includes('pcm') || mimeType.includes('rate=')
  )
}

function isVisualMimeType(mimeType: string) {
  return mimeType.startsWith('image/') || mimeType.startsWith('video/')
}

function cleanSpeechText(rawText: string): string {
  return (rawText || '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

type TranscriptDraftState = {
  user: string
  model: string
}

type TranscriptRole = keyof TranscriptDraftState
type ModelTranscriptSource = 'outputTranscript' | 'modelText' | null

export function extractUrlsFromText(text: string): string[] {
  return Array.from(text.matchAll(URL_REGEX), match => match[1])
    .filter((url): url is string => Boolean(url))
    .map(url => url.replace(/[),.;!?]+$/, ''))
}

export function buildLiveUrlContextUpdate(
  text: string,
  urlContext: string,
  capturedAt: number
): LiveContextUpdate | null {
  const urls = extractUrlsFromText(text)
  const analysis = urlContext.trim()
  if (urls.length === 0 || !analysis) return null

  return {
    modality: 'intelligence',
    analysis: `URL context from the user's latest turn:\n\n${analysis}`,
    capturedAt,
    metadata: {
      urlAnalysis: analysis,
      urls,
    },
  }
}

function languageCodeFromSnapshot(snapshot: {
  uiLocale?: unknown
  uiLocaleExplicit?: unknown
  language?: unknown
  livePolicy?: unknown
}): string {
  const livePolicy = snapshot.livePolicy
  const livePolicyLanguageCode =
    livePolicy && typeof livePolicy === 'object' && !Array.isArray(livePolicy)
      ? (livePolicy as { languageCode?: unknown }).languageCode
      : undefined
  const uiLocale = snapshot.uiLocale === 'nb' || snapshot.uiLocale === 'en' ? snapshot.uiLocale : undefined
  return uiLocaleToLanguageCode(resolveUiLocale({
    uiLocale,
    uiLocaleExplicit: snapshot.uiLocaleExplicit === true,
    language: typeof snapshot.language === 'string' ? snapshot.language : undefined,
    livePolicyLanguageCode: typeof livePolicyLanguageCode === 'string' ? livePolicyLanguageCode : undefined,
  }))
}

interface UseLiveSessionBridgeProps {
  sessionId: string
  clientSessionViewStore: {
    getSnapshot: () => Record<string, any>
    getServerProjectionSnapshot?: () => SessionProjectionEnvelopeV1 | null
    upsertOptimisticTranscriptItem?: (item: TranscriptItem) => void
  }
  livePolicy: LiveSessionPolicy
  isWebcamActive: boolean
  visualModalityStateRef?: MutableRefObject<{ webcam: boolean; screen: boolean }>
  setActiveRoute: (route: ModelRoute) => void
  setIsChatVisible: (visible: boolean) => void
  setVisualState: Dispatch<SetStateAction<VisualState>>
  setActiveTools?: React.Dispatch<React.SetStateAction<any[]>>
  handleVolumeChange: (input: number, output: number) => void
  handleTranscript: (
    text: string,
    isUser: boolean,
    isFinal: boolean,
    grounding?: unknown,
    agent?: unknown,
    turnId?: string
  ) => void
  handleToolCall: (calls: LiveToolCall[]) => Promise<any[]>
  onSignal?: (payload: { action: string; turnId?: string; data?: unknown }) => void
  /** Voice/Live user asked for the brief — client auto-runs prepare (progress UI) even if the model has not called the tool yet. */
  onUserDiscoveryReportIntent?: (text: string) => void
  onOrchestratorGateStageUpdate?: (payload: {
    stage?: string
    agent?: string
    metadata?: Record<string, unknown>
    turnId?: string
  }) => void
  onOrchestratorGateTurnComplete?: (payload: {
    turnId?: string
    source?: string
    text: string
  }) => void
  /** True while a Live-first gate loading row is active (orchestrator path pending). */
  isGateTurnActive?: () => boolean
  liveServiceRef: MutableRefObject<LiveRuntimeController | null>
  onLiveSessionEnd?: () => void
}

/** mintDisplayTurnId appends «#n». The server turn id is the part before that suffix. */
export function canonicalTurnId(displayId: string | null | undefined): string {
  if (!displayId) return ''
  return displayId.trim().replace(/#\d+$/, '')
}

/**
 * Keep the open agent row only for the late first YOU line of this turn.
 * That is the same turn, no YOU row yet, and a partial that has not ended.
 * A second line on this turn, a sentence that already ends, another turn,
 * or a missing turnId closes the row. Fly can drop turn_complete, so a
 * different turnId still closes it.
 */
function shouldFinalizeOpenModelForInput(
  modelDisplayId: string | null,
  modelDraft: string,
  eventTurnId: string | undefined,
  turnAlreadyHasUserRow: boolean,
): boolean {
  const draft = modelDraft.trim()
  const open = Boolean(modelDisplayId || draft)
  if (!open) return false
  if (!eventTurnId) return true
  const sameTurn = Boolean(modelDisplayId) && canonicalTurnId(modelDisplayId) === eventTurnId
  const lateFirstLine =
    sameTurn &&
    !turnAlreadyHasUserRow &&
    Boolean(draft) &&
    !looksLikeCompletedUtterance(draft)
  return !lateFirstLine
}

export function useLiveSessionBridge({
  sessionId,
  clientSessionViewStore,
  livePolicy,
  isWebcamActive,
  visualModalityStateRef: _visualModalityStateRef,
  setActiveRoute,
  setIsChatVisible,
  setVisualState,
  setActiveTools,
  handleVolumeChange,
  handleTranscript,
  handleToolCall,
  onSignal,
  onUserDiscoveryReportIntent,
  onOrchestratorGateStageUpdate,
  onOrchestratorGateTurnComplete,
  isGateTurnActive,
  liveServiceRef,
  onLiveSessionEnd,
}: UseLiveSessionBridgeProps) {
  const { showToast } = useToast()
  const [connectionState, setConnectionState] = useState<LiveConnectionState>(
    getStoredConnectionState
  )
  const [isAudioInputStarting, setIsAudioInputStarting] = useState(false)
  const connectionStateRef = useRef(connectionState)
  const connectingRef = useRef(false)
  const handleConnectRef = useRef<(opts?: { audio?: boolean }) => Promise<void>>(() =>
    Promise.resolve()
  )
  const audioActivationInFlightRef = useRef(false)
  const transcriptDraftsRef = useRef<TranscriptDraftState>({ user: '', model: '' })
  const lastFinalTextRef = useRef<{ user: string; model: string }>({ user: '', model: '' })
  /** Display turn that already committed lastFinalTextRef. A new turn with the same words is a new row. */
  const lastFinalTurnIdRef = useRef<{ user: string | null; model: string | null }>({
    user: null,
    model: null,
  })
  const livePolicyRef = useRef(livePolicy)
  const lastSentPolicyJsonRef = useRef<string | null>(null)
  const visualStreamActiveRef = useRef(isWebcamActive)
  const activeTurnIdsRef = useRef<{ user: string | null; model: string | null }>({
    user: null,
    model: null,
  })
  /** One visible transcript row per minted display turnId; canonical gateway turnId stays separate. */
  const displayTurnIdRef = useRef<{ user: string | null; model: string | null }>({
    user: null,
    model: null,
  })
  const displayTurnSeqRef = useRef(0)
  /**
   * One writer per turn: the provisional YOU row (ASR partials without a server
   * turnId) that the server's final transcript must replace, not duplicate.
   */
  const awaitingServerFinalUserRowRef = useRef<string | null>(null)
  /** Row of the last server user turnId: later partials and the final of that turn land on it. */
  const serverUserTurnRowRef = useRef<{ turnId: string; displayTurnId: string; text: string; modelMark: string } | null>(null)
  const lastReleasedDisplayTurnIdRef = useRef<{ user: string | null; model: string | null }>({
    user: null,
    model: null,
  })
  const lastClosedModelTextRef = useRef('')
  const userAsrPauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const modelTranscriptSourceRef = useRef<{
    turnId: string | null
    source: ModelTranscriptSource
  }>({ turnId: null, source: null })
  const runtimeOwnershipRef = useRef<ReturnType<LiveRuntimeController['getRuntimeOwnershipState']>>({
    ownsTurns: false,
    reason: 'none',
  })
  const lastFinalUserPromptRef = useRef<string | null>(null)
  const skippedVisualFrameLogAtRef = useRef(0)
  const liveToolIdRef = useRef<string | null>(null)
  const gateTurnTextRef = useRef('')
  const lastGateTurnIdRef = useRef<string | null>(null)
  const lastUrlAnalysisByUrlRef = useRef(new Map<string, string>())
  const liveSessionRef = useRef<ReturnType<typeof useLiveSession> | null>(null)
  const suppressStartupModelOutputRef = useRef(false)
  const startupSuppressionReasonRef = useRef<string | null>(null)
  /** Typed sendText keeps Live playback muted until a mic / speakAgentOutput turn. */
  const typedOutputMuteRef = useRef(false)
  const firstConsultingSpokenRef = useRef<string | null>(readStoredFirstConsultingLine())
  const firstConsultingWatchRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const correctionBindSentRef = useRef<string | null>(null)
  const onUserDiscoveryReportIntentRef = useRef(onUserDiscoveryReportIntent)
  onUserDiscoveryReportIntentRef.current = onUserDiscoveryReportIntent

  const markLiveRuntimeOwner = useCallback(
    (reason: ReturnType<LiveRuntimeController['getRuntimeOwnershipState']>['reason']) => {
      runtimeOwnershipRef.current =
        reason === 'none' ? { ownsTurns: false, reason } : { ownsTurns: true, reason }
    },
    []
  )

  const mintDisplayTurnId = useCallback((role: TranscriptRole, canonicalTurnId?: string) => {
    const base = canonicalTurnId?.trim() || `live-${role}`
    displayTurnSeqRef.current += 1
    return `${base}#${displayTurnSeqRef.current}`
  }, [])

  const resolveDisplayTurnId = useCallback(
    (role: TranscriptRole, canonicalTurnId?: string) => {
      const current = displayTurnIdRef.current[role]
      if (current) return current
      const next = mintDisplayTurnId(role, canonicalTurnId)
      displayTurnIdRef.current[role] = next
      return next
    },
    [mintDisplayTurnId]
  )

  const releaseDisplayTurn = useCallback((role: TranscriptRole) => {
    lastReleasedDisplayTurnIdRef.current[role] = displayTurnIdRef.current[role]
    displayTurnIdRef.current[role] = null
  }, [])

  const clearUserAsrPauseTimer = useCallback(() => {
    if (!userAsrPauseTimerRef.current) return
    clearTimeout(userAsrPauseTimerRef.current)
    userAsrPauseTimerRef.current = null
  }, [])

  const resetDisplayTurnIds = useCallback(() => {
    displayTurnIdRef.current = { user: null, model: null }
    lastReleasedDisplayTurnIdRef.current = { user: null, model: null }
    lastClosedModelTextRef.current = ''
    displayTurnSeqRef.current = 0
    clearUserAsrPauseTimer()
  }, [clearUserAsrPauseTimer])

  const releaseUserDisplayTurnIfClosed = useCallback(() => {
    if (transcriptDraftsRef.current.user.trim()) return
    if (!displayTurnIdRef.current.user) return
    releaseDisplayTurn('user')
  }, [releaseDisplayTurn])

  const beginStartupModelOutputSuppression = useCallback((reason: string) => {
    startupSuppressionReasonRef.current = reason
    suppressStartupModelOutputRef.current = true
    transcriptDraftsRef.current.model = ''
    modelTranscriptSourceRef.current = { turnId: null, source: null }
    logger.debug('[useLiveSessionBridge] suppressing Live startup model output', { reason })
    liveRuntimeTrace({
      event: 'suppression_begin',
      suppressionActive: true,
      suppressionReason: reason,
      runtimeOwner: runtimeOwnershipRef.current.reason,
      sessionPrefix: sessionIdPrefix(sessionId),
    })
  }, [sessionId])

  const clearStartupModelOutputSuppression = useCallback((clearTrigger: string) => {
    const activeReason = startupSuppressionReasonRef.current
    if (!activeReason) return

    startupSuppressionReasonRef.current = null
    suppressStartupModelOutputRef.current = false
    logger.debug('[useLiveSessionBridge] cleared Live startup model output suppression', {
      reason: activeReason,
      clearTrigger,
    })
    liveRuntimeTrace({
      event: 'suppression_clear',
      suppressionActive: false,
      suppressionReason: activeReason,
      runtimeOwner: runtimeOwnershipRef.current.reason,
      sessionPrefix: sessionIdPrefix(sessionId),
      detail: clearTrigger,
    })
  }, [sessionId])

  const stopFirstConsultingWatch = useCallback(() => {
    if (!firstConsultingWatchRef.current) return
    clearInterval(firstConsultingWatchRef.current)
    firstConsultingWatchRef.current = null
  }, [])

  const speakFirstConsultingLineFromGreeting = useCallback((): boolean => {
    if (livePolicyRef.current.responseMode === 'listen_only') return false
    if (
      connectionStateRef.current !== LiveConnectionState.CONNECTED &&
      !liveSessionRef.current?.adapter.getIsSessionReady()
    ) {
      return false
    }

    const snapshot = clientSessionViewStore.getSnapshot() as { transcript?: Array<{ id?: string; role?: string; text?: string | null }> }
    const transcript = snapshot.transcript || []
    const labeled = readLabeledGuessGreeting(transcript)
    const line = labeled || readLatestGreeting(transcript)
    const visitorHasSpoken = transcriptHasVisitorTurn(transcript)
    if (visitorHasSpoken) {
      const lock = firstConsultingSpokenRef.current || line
      if (lock) {
        firstConsultingSpokenRef.current = lock
        persistFirstConsultingLine(lock)
      }
      return true
    }
    if (!line) return false

    const session = liveSessionRef.current
    if (!session?.adapter.getIsSessionReady()) return false

    const alreadySpoken = firstConsultingSpokenRef.current
    const richerLineReady =
      Boolean(labeled) &&
      normalizeGreetingCompareText(alreadySpoken) === normalizeGreetingCompareText(labeled) &&
      !isCannedBottleneckWelcome(labeled)
    if (!shouldSpeakFirstConsultingLine({
      alreadySpoken,
      nextLine: line,
      visitorHasSpoken: false,
    })) {
      // Presence or a canned line can still be replaced before they speak.
      return richerLineReady
    }

    firstConsultingSpokenRef.current = line
    persistFirstConsultingLine(line)
    typedOutputMuteRef.current = false
    session.setAudioOutputMuted(false)
    session.adapter.sendRealtimeText(`[SAY] ${line}`)
    liveRuntimeTrace({
      event: 'speakAgentOutput',
      route: 'live',
      runtimeOwner: runtimeOwnershipRef.current.reason,
      sessionPrefix: sessionIdPrefix(sessionId),
      textLen: line.length,
      textPreview: redactTraceText(line),
      detail: 'first_consulting_line',
    })
    return Boolean(labeled) && !isCannedBottleneckWelcome(line)
  }, [clientSessionViewStore, sessionId])

  const watchFirstConsultingLine = useCallback(() => {
    if (speakFirstConsultingLineFromGreeting()) {
      stopFirstConsultingWatch()
      return
    }
    if (firstConsultingWatchRef.current) return
    const startedAt = Date.now()
    firstConsultingWatchRef.current = setInterval(() => {
      if (
        speakFirstConsultingLineFromGreeting() ||
        Date.now() - startedAt > FIRST_CONSULTING_WATCH_MS
      ) {
        stopFirstConsultingWatch()
      }
    }, 400)
  }, [speakFirstConsultingLineFromGreeting, stopFirstConsultingWatch])

  const bindHypothesisCorrection = useCallback((visitorText: string) => {
    const session = liveSessionRef.current
    if (!session?.adapter.getIsSessionReady()) return
    const bind = formatHypothesisCorrectionBind(visitorText)
    if (correctionBindSentRef.current === bind) return
    correctionBindSentRef.current = bind
    session.adapter.sendRealtimeText(bind)
  }, [])

  useEffect(() => {
    connectionStateRef.current = connectionState
    try {
      sessionStorage.setItem(FBC_LIVE_CONNECTION_STORAGE_KEY, connectionState)
    } catch {
      /* ignore */
    }
  }, [connectionState])

  useEffect(() => {
    livePolicyRef.current = livePolicy
  }, [livePolicy])

  useEffect(() => {
    return () => {
      if (userAsrPauseTimerRef.current) {
        clearTimeout(userAsrPauseTimerRef.current)
        userAsrPauseTimerRef.current = null
      }
      if (firstConsultingWatchRef.current) {
        clearInterval(firstConsultingWatchRef.current)
        firstConsultingWatchRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (isWebcamActive) {
      visualStreamActiveRef.current = true
      markLiveRuntimeOwner('visual_stream')
    }
  }, [isWebcamActive, markLiveRuntimeOwner])

  const analyzeUrlTurnForLiveContext = useCallback(
    async (text: string) => {
      const urls = extractUrlsFromText(text)
      if (urls.length === 0) return

      let urlContext = ''
      try {
        urlContext = await detectAndAnalyzeUrlsWithBudget(text)
      } catch (error) {
        logger.warn('[useLiveSessionBridge] Live user-turn URL analysis failed', {
          error: error instanceof Error ? error.message : String(error),
        })
      }

      if (!urlContext) return

      for (const url of urls) {
        if (lastUrlAnalysisByUrlRef.current.get(url) === urlContext) continue
        lastUrlAnalysisByUrlRef.current.set(url, urlContext)
      }

      const contextUpdate = buildLiveUrlContextUpdate(text, urlContext, Date.now())
      if (
        contextUpdate &&
        (connectionStateRef.current === LiveConnectionState.CONNECTED ||
          connectionStateRef.current === LiveConnectionState.CONNECTING ||
          liveSessionRef.current?.adapter.getIsSessionReady())
      ) {
        liveSessionRef.current?.adapter.sendContextUpdate(contextUpdate)
      }
    },
    [clientSessionViewStore]
  )

  const emitTranscriptSnapshot = useCallback(
    (
      role: TranscriptRole,
      nextText: string,
      isFinal: boolean,
      grounding?: unknown,
      suppliedTurnId?: string
    ) => {
      // The bind is model context. Live echoes the outbound user turn, so strip
      // it for both roles before it can paint a YOU row.
      const normalizedText = stripHypothesisInstructionLeak(nextText).trim()
      if (!normalizedText || (role === 'user' && isLeakOpenerFragment(normalizedText))) {
        if (isFinal) transcriptDraftsRef.current[role] = ''
        return
      }
      if (role === 'user' && isInjectedSayPrompt(normalizedText)) {
        if (isFinal) transcriptDraftsRef.current[role] = ''
        return
      }
      if (role === 'model' && isGenericLiveImprovWelcome(normalizedText)) {
        if (isFinal) transcriptDraftsRef.current[role] = ''
        void speakFirstConsultingLineFromGreeting()
        return
      }
      if (
        role === 'model' &&
        firstConsultingSpokenRef.current &&
        transcriptHasVisitorTurn(
          (clientSessionViewStore.getSnapshot() as { transcript?: Array<{ role?: string; text?: string | null }> })
            .transcript || []
        ) &&
        normalizeGreetingCompareText(normalizedText) ===
          normalizeGreetingCompareText(firstConsultingSpokenRef.current)
      ) {
        if (isFinal) transcriptDraftsRef.current[role] = ''
        return
      }

      const turnId = suppliedTurnId?.trim() || activeTurnIdsRef.current[role] || undefined

      if (
        isFinal &&
        lastFinalTextRef.current[role] === normalizedText &&
        turnId &&
        lastFinalTurnIdRef.current[role] === turnId
      ) {
        return
      }

      liveRuntimeTrace({
        event: 'transcript_append',
        appendSource: 'bridge:emitTranscriptSnapshot',
        eventType: role === 'user' ? 'inputTranscript' : 'outputTranscript',
        suppressionActive: suppressStartupModelOutputRef.current,
        runtimeOwner: runtimeOwnershipRef.current.reason,
        sessionPrefix: sessionIdPrefix(sessionId),
        textLen: normalizedText.length,
        textPreview: redactTraceText(normalizedText),
        isFinal,
        ...(turnId ? { extra: { turnId } } : {}),
      })
      if (turnId) {
        handleTranscript(normalizedText, role === 'user', isFinal, grounding, undefined, turnId)
      } else {
        handleTranscript(normalizedText, role === 'user', isFinal, grounding, undefined)
      }

      if (isFinal) {
        lastFinalTextRef.current[role] = normalizedText
        lastFinalTurnIdRef.current[role] = turnId ?? null
      }

      if (isFinal && role === 'user') {
        lastFinalUserPromptRef.current = normalizedText || null
        void analyzeUrlTurnForLiveContext(normalizedText)
        if (isDiscoveryReportRequest(normalizedText)) {
          onUserDiscoveryReportIntentRef.current?.(normalizedText)
        }
        if (isHypothesisCorrection(normalizedText)) {
          bindHypothesisCorrection(normalizedText)
        }
      }

      transcriptDraftsRef.current[role] = isFinal ? '' : normalizedText
    },
    [
      analyzeUrlTurnForLiveContext,
      bindHypothesisCorrection,
      handleTranscript,
      sessionId,
      speakFirstConsultingLineFromGreeting,
    ]
  )

  const finalizeOpenUserUtterance = useCallback(() => {
    clearUserAsrPauseTimer()
    const draft = transcriptDraftsRef.current.user.trim()
    const turnId =
      displayTurnIdRef.current.user ??
      lastReleasedDisplayTurnIdRef.current.user ??
      activeTurnIdsRef.current.user ??
      undefined
    if (draft) {
      emitTranscriptSnapshot('user', draft, true, undefined, turnId)
    }
    transcriptDraftsRef.current.user = ''
    if (displayTurnIdRef.current.user) {
      if (!activeTurnIdsRef.current.user) {
        // Client-side endpoint only. The server final for this utterance lands on this row.
        awaitingServerFinalUserRowRef.current = displayTurnIdRef.current.user
      }
      releaseDisplayTurn('user')
    }
  }, [clearUserAsrPauseTimer, emitTranscriptSnapshot, releaseDisplayTurn])

  const scheduleUserAsrEndpoint = useCallback(() => {
    clearUserAsrPauseTimer()
    userAsrPauseTimerRef.current = setTimeout(() => {
      userAsrPauseTimerRef.current = null
      finalizeOpenUserUtterance()
    }, LIVE_USER_ASR_ENDPOINT_PAUSE_MS)
  }, [clearUserAsrPauseTimer, finalizeOpenUserUtterance])

  const restoreUserDisplayTurnIfGrowingRevision = useCallback(
    (incoming: string, serverTurnId?: string) => {
      if (displayTurnIdRef.current.user) return
      const lastId = lastReleasedDisplayTurnIdRef.current.user
      const priorFinal = lastFinalTextRef.current.user.trim()
      if (!lastId || !priorFinal || !incoming.trim()) return
      if (!isGrowingUserAsrRevision(priorFinal, incoming)) return
      // «Hallo?» after «Hallo?» on a new server turn is a barge-in, not a longer ASR draft.
      const previousServerTurn = serverUserTurnRowRef.current?.turnId
      if (serverTurnId && previousServerTurn && serverTurnId !== previousServerTurn) return
      displayTurnIdRef.current.user = lastId
    },
    [],
  )

  const finalizeOpenModelGeneration = useCallback(() => {
    const draft = transcriptDraftsRef.current.model.trim()
    const turnId =
      displayTurnIdRef.current.model ??
      activeTurnIdsRef.current.model ??
      undefined
    if (draft) {
      emitTranscriptSnapshot('model', draft, true, undefined, turnId)
    }
    const committed = draft || lastFinalTextRef.current.model.trim()
    const seenUser = lastFinalUserPromptRef.current?.trim() ?? ''
    if (sessionId && seenUser && committed && !/^\s*\[SAY\]/i.test(committed)) {
      void rememberSeenAssistantReply(sessionId, seenUser, committed)
    }
    lastClosedModelTextRef.current = committed || lastClosedModelTextRef.current
    transcriptDraftsRef.current.model = ''
    lastFinalTextRef.current.model = ''
    modelTranscriptSourceRef.current = { turnId: null, source: null }
    activeTurnIdsRef.current.model = null
    // Hold the display id for a one-word stub so late outputTranscript
    // revises the same row instead of minting like? / process? bubbles.
    if (!committed || !isShortModelStub(committed)) {
      releaseDisplayTurn('model')
    }
  }, [emitTranscriptSnapshot, releaseDisplayTurn, sessionId])

  const liveSession = useLiveSession({
    onToolCall: calls => handleToolCall(calls),
    onVolumeChange: handleVolumeChange,
    onStateChange: state => {
      const nextConnectionState = mapState(state)
      setConnectionState(nextConnectionState)
      setVisualState(prev => ({ ...prev, isActive: nextConnectionState === LiveConnectionState.CONNECTED }))
      if (
        nextConnectionState === LiveConnectionState.DISCONNECTED ||
        nextConnectionState === LiveConnectionState.ERROR
      ) {
        markLiveRuntimeOwner('none')
        audioActivationInFlightRef.current = false
        setIsAudioInputStarting(false)
        onLiveSessionEnd?.()
        setActiveRoute(
          createReadyRoute(
            nextConnectionState === LiveConnectionState.ERROR
              ? 'Live connection failed. Text chat remains available.'
              : 'System ready.'
          )
        )
      }
      if (state !== 'connecting' && state !== 'reconnecting') {
        connectingRef.current = false
      }
    },
    onToolStatus: (status, toolName) => {
      setVisualState(prev => ({
        ...prev,
        toolCallData: {
          name: toolName,
          progress: status === 'executing' ? 0.5 : 1,
          state: status === 'executing' ? 'running' as const : 'complete' as const,
        },
      }))

      if (!setActiveTools) return

      if (status === 'executing') {
        const id = generateTranscriptId(`tool-${toolName}`)
        liveToolIdRef.current = id
        setActiveTools(prev => {
          const existing = prev.find((t: any) => t.id === id)
          if (existing) return prev
          return [
            ...prev,
            {
              id,
              name: toolName,
              status: 'running',
              startTime: Date.now(),
            },
          ]
        })
      } else if (status === 'complete') {
        const id = liveToolIdRef.current
        if (id) {
          setActiveTools(prev =>
            prev.map((t: any) =>
              t.id === id ? { ...t, status: 'complete', endTime: Date.now() } : t
            )
          )
          liveToolIdRef.current = null
        } else {
          // Fallback: match by most recent running tool with same name
          setActiveTools(prev => {
            const running = prev
              .filter((t: any) => t.status === 'running' && t.name === toolName)
              .sort((a: any, b: any) => (b.startTime || 0) - (a.startTime || 0))
            const mostRecent = running[0]
            if (!mostRecent) return prev
            return prev.map((t: any) =>
              t.id === mostRecent.id ? { ...t, status: 'complete', endTime: Date.now() } : t
            )
          })
        }
      }
    },
    onError: error => {
      if (error.code !== LIVE_LINE_UNCONFIRMED) return
      const code = (livePolicyRef.current.languageCode || '').toLowerCase()
      const norwegian = code.startsWith('nb') || code.startsWith('nn') || code === 'no'
      showToast(
        norwegian ? nbChat.composerStatus.lineNotStored : enChat.composerStatus.lineNotStored,
        'error',
      )
    },
    onEvent: (event: LiveSessionEvent) => {
      liveRuntimeTrace({
        event: 'live_adapter_event',
        eventType: event.type,
        suppressionActive: suppressStartupModelOutputRef.current,
        runtimeOwner: runtimeOwnershipRef.current.reason,
        sessionPrefix: sessionIdPrefix(sessionId),
        textLen: 'text' in event && typeof event.text === 'string' ? event.text.length : undefined,
        textPreview:
          'text' in event && typeof event.text === 'string'
            ? redactTraceText(event.text)
            : undefined,
        isFinal: 'isFinal' in event ? Boolean(event.isFinal) : undefined,
      })
      switch (event.type) {
        case 'connected':
          markLiveRuntimeOwner('connected')
          transcriptDraftsRef.current = { user: '', model: '' }
          awaitingServerFinalUserRowRef.current = null
          serverUserTurnRowRef.current = null
          lastFinalTextRef.current = { user: '', model: '' }
          activeTurnIdsRef.current = { user: null, model: null }
          resetDisplayTurnIds()
          modelTranscriptSourceRef.current = { turnId: null, source: null }
          break

        case 'sessionReady':
          markLiveRuntimeOwner('session_ready')
          break

        case 'sessionProjection':
          // acceptServerProjection rebuilds the transcript. On sess_52cbe224 that
          // put the canonical rows above YOU and the opener. Facts only.
          acceptLiveSessionFactsEnvelope(event.envelope, 'live-gateway')
          break

        case 'inputTranscript': {
          const trimmedInput = event.text?.trim() ?? ''
          if (isInjectedSayPrompt(trimmedInput)) {
            break
          }
          if (trimmedInput) {
            clearStartupModelOutputSuppression('live_user_input')
          }
          const eventTurnId = event.turnId?.trim()
          const turnAlreadyHasUserRow = Boolean(
            eventTurnId && serverUserTurnRowRef.current?.turnId === eventTurnId,
          )
          const modelMark = `${lastClosedModelTextRef.current}\u0000${transcriptDraftsRef.current.model.trim()}`
          const candidateRow =
            eventTurnId && serverUserTurnRowRef.current?.turnId === eventTurnId
              ? serverUserTurnRowRef.current
              : null
          const finalCoversRow = Boolean(
            candidateRow &&
              event.isFinal &&
              trimmedInput &&
              (trimmedInput.includes(candidateRow.text) || candidateRow.text.includes(trimmedInput))
          )
          // Same row only while the model has not answered, or for the final that
          // carries this row's text. A new line after a reply stays a new row.
          const sameServerTurnRow =
            candidateRow && (finalCoversRow || candidateRow.modelMark === modelMark)
              ? candidateRow
              : null
          if (sameServerTurnRow) {
            // One server turnId is one YOU row. Gemini can put two utterances in one
            // turn and send each as its own partial; the final carries the merged text.
            clearUserAsrPauseTimer()
            awaitingServerFinalUserRowRef.current = null
            if (
              shouldFinalizeOpenModelForInput(
                displayTurnIdRef.current.model,
                transcriptDraftsRef.current.model,
                eventTurnId,
                turnAlreadyHasUserRow,
              )
            ) {
              finalizeOpenModelGeneration()
            }
            displayTurnIdRef.current.user = sameServerTurnRow.displayTurnId
            activeTurnIdsRef.current.user = sameServerTurnRow.turnId
            const prior = sameServerTurnRow.text
            const merged = event.isFinal
              ? trimmedInput || prior
              : mergeLiveTranscriptSnapshot(prior, trimmedInput)
            const rowText =
              !event.isFinal && trimmedInput && !merged.includes(trimmedInput)
                ? `${prior} ${trimmedInput}`.trim()
                : merged === prior && trimmedInput && !prior.includes(trimmedInput)
                  ? `${prior} ${trimmedInput}`.trim()
                  : merged
            sameServerTurnRow.text = rowText
            if (event.isFinal) {
              transcriptDraftsRef.current.user = ''
              if (rowText) emitTranscriptSnapshot('user', rowText, true, undefined, sameServerTurnRow.displayTurnId)
              releaseDisplayTurn('user')
            } else {
              transcriptDraftsRef.current.user = rowText
              if (rowText) emitTranscriptSnapshot('user', rowText, false, undefined, sameServerTurnRow.displayTurnId)
              scheduleUserAsrEndpoint()
            }
            break
          }
          const serverFinal = Boolean(event.isFinal && eventTurnId)
          const provisionalRow =
            displayTurnIdRef.current.user && !activeTurnIdsRef.current.user
              ? displayTurnIdRef.current.user
              : awaitingServerFinalUserRowRef.current
          if (serverFinal && provisionalRow) {
            // The server owns the committed YOU row. Replace the provisional
            // partial row instead of committing the partials as their own row.
            clearUserAsrPauseTimer()
            displayTurnIdRef.current.user = provisionalRow
            transcriptDraftsRef.current.user = ''
            awaitingServerFinalUserRowRef.current = null
          } else if (
            eventTurnId &&
            activeTurnIdsRef.current.user &&
            eventTurnId !== activeTurnIdsRef.current.user
          ) {
            finalizeOpenUserUtterance()
          }
          if (!eventTurnId && !event.isFinal && !displayTurnIdRef.current.user && awaitingServerFinalUserRowRef.current) {
            // More speech before the server final: keep growing the same provisional row.
            displayTurnIdRef.current.user = awaitingServerFinalUserRowRef.current
            awaitingServerFinalUserRowRef.current = null
          }
          const inputTurnId = eventTurnId || activeTurnIdsRef.current.user || undefined
          if (inputTurnId) activeTurnIdsRef.current.user = inputTurnId
          if (
            shouldFinalizeOpenModelForInput(
              displayTurnIdRef.current.model,
              transcriptDraftsRef.current.model,
              eventTurnId,
              turnAlreadyHasUserRow,
            )
          ) {
            finalizeOpenModelGeneration()
          }
          const openUserDraft = transcriptDraftsRef.current.user.trim()
          if (
            openUserDraft &&
            trimmedInput &&
            // Provisional partials (no server turnId) never split; the server final replaces them.
            Boolean(eventTurnId) &&
            looksLikeCompletedUtterance(openUserDraft) &&
            !isGrowingUserAsrRevision(openUserDraft, trimmedInput)
          ) {
            finalizeOpenUserUtterance()
          }
          restoreUserDisplayTurnIfGrowingRevision(trimmedInput, eventTurnId)
          const displayTurnId = resolveDisplayTurnId('user', inputTurnId)
          const currentUserText = mergeLiveTranscriptSnapshot(
            transcriptDraftsRef.current.user,
            event.text ?? ''
          )
          if (eventTurnId) {
            serverUserTurnRowRef.current = {
              turnId: eventTurnId,
              displayTurnId,
              text: currentUserText || trimmedInput,
              modelMark: `${lastClosedModelTextRef.current}\u0000${transcriptDraftsRef.current.model.trim()}`,
            }
          }
          if (currentUserText) {
            transcriptDraftsRef.current.user = currentUserText
          }
          if (event.isFinal) {
            const finalText =
              currentUserText ||
              trimmedInput ||
              transcriptDraftsRef.current.user.trim() ||
              lastFinalUserPromptRef.current ||
              ''
            if (finalText) {
              emitTranscriptSnapshot('user', finalText, true, undefined, displayTurnId)
              releaseDisplayTurn('user')
            }
            clearUserAsrPauseTimer()
          } else if (currentUserText) {
            emitTranscriptSnapshot('user', currentUserText, false, undefined, displayTurnId)
            scheduleUserAsrEndpoint()
          }
          break
        }

        case 'text': {
          if (isGenericLiveImprovWelcome(event.text)) {
            void speakFirstConsultingLineFromGreeting()
            break
          }
          if (event.source === 'orchestrator_gate' && event.text?.trim()) {
            gateTurnTextRef.current = event.text
            if (event.turnId) lastGateTurnIdRef.current = event.turnId
            break
          }
          if (suppressStartupModelOutputRef.current && event.text?.trim()) {
            liveRuntimeTrace({
              event: 'model_output_suppressed',
              eventType: 'text',
              suppressionActive: true,
              suppressionReason: startupSuppressionReasonRef.current ?? 'startup',
              runtimeOwner: runtimeOwnershipRef.current.reason,
              sessionPrefix: sessionIdPrefix(sessionId),
              textLen: event.text.length,
              textPreview: redactTraceText(event.text),
            })
            logger.debug('[useLiveSessionBridge] suppressed startup model text', {
              text: event.text.slice(0, 80),
            })
            break
          }
          const incomingModelText = event.text?.trim() ?? ''
          if (
            !displayTurnIdRef.current.model &&
            isClosedModelOutputReplay(lastClosedModelTextRef.current, incomingModelText)
          ) {
            break
          }
          const canonicalTurnId =
            event.turnId?.trim() ||
            activeTurnIdsRef.current.model ||
            activeTurnIdsRef.current.user ||
            undefined
          const displayTurnId = resolveDisplayTurnId('model', canonicalTurnId)
          if (
            modelTranscriptSourceRef.current.source === 'outputTranscript' &&
            modelTranscriptSourceRef.current.turnId === displayTurnId
          ) {
            logger.debug('[useLiveSessionBridge] skipping model text after output transcript', {
              chunk: event.text?.slice(0, 40),
              turnId: displayTurnId,
            })
            break
          }
          if (modelTranscriptSourceRef.current.turnId !== displayTurnId) {
            modelTranscriptSourceRef.current = { turnId: displayTurnId, source: null }
            transcriptDraftsRef.current.model = ''
            lastFinalTextRef.current.model = ''
          }
          modelTranscriptSourceRef.current.source = 'modelText'
          const currentModelText = mergeLiveTranscriptSnapshot(
            transcriptDraftsRef.current.model,
            event.text
          )
          logger.debug('[useLiveSessionBridge] text chunk', {
            chunk: event.text?.slice(0, 40),
            length: event.text?.length,
          })
          emitTranscriptSnapshot(
            'model',
            currentModelText,
            false,
            undefined,
            displayTurnId
          )
          break
        }

        case 'outputTranscript': {
          if (isGenericLiveImprovWelcome(event.text)) {
            void speakFirstConsultingLineFromGreeting()
            break
          }
          if (event.source === 'orchestrator_gate') {
            if (event.text?.trim()) {
              gateTurnTextRef.current = event.text
              if (event.turnId) lastGateTurnIdRef.current = event.turnId
            }
            break
          }
          if (suppressStartupModelOutputRef.current && event.text?.trim()) {
            liveRuntimeTrace({
              event: 'model_output_suppressed',
              eventType: 'outputTranscript',
              suppressionActive: true,
              suppressionReason: startupSuppressionReasonRef.current ?? 'startup',
              runtimeOwner: runtimeOwnershipRef.current.reason,
              sessionPrefix: sessionIdPrefix(sessionId),
              textLen: event.text.length,
              textPreview: redactTraceText(event.text),
              isFinal: event.isFinal,
            })
            logger.debug('[useLiveSessionBridge] suppressed startup output transcript', {
              text: event.text.slice(0, 80),
              isFinal: event.isFinal,
            })
            if (event.isFinal) {
              transcriptDraftsRef.current.model = ''
              modelTranscriptSourceRef.current = { turnId: null, source: null }
            }
            break
          }
          if (looksLikeCompletedUtterance(transcriptDraftsRef.current.user)) {
            finalizeOpenUserUtterance()
          } else {
            releaseUserDisplayTurnIfClosed()
          }
          const incomingOutput = event.text?.trim() ?? ''
          if (
            !displayTurnIdRef.current.model &&
            isClosedModelOutputReplay(lastClosedModelTextRef.current, incomingOutput)
          ) {
            break
          }
          const canonicalTurnId =
            event.turnId?.trim() ||
            activeTurnIdsRef.current.model ||
            activeTurnIdsRef.current.user ||
            undefined
          const displayTurnId = resolveDisplayTurnId('model', canonicalTurnId)
          if (modelTranscriptSourceRef.current.turnId !== displayTurnId) {
            transcriptDraftsRef.current.model = ''
            lastFinalTextRef.current.model = ''
          }
          modelTranscriptSourceRef.current = {
            turnId: displayTurnId,
            source: 'outputTranscript',
          }
          const currentOutputText = mergeLiveTranscriptSnapshot(
            transcriptDraftsRef.current.model,
            event.text
          )
          logger.debug('[useLiveSessionBridge] outputTranscript', {
            text: event.text?.slice(0, 40),
            length: event.text?.length,
            isFinal: event.isFinal,
          })
          emitTranscriptSnapshot(
            'model',
            currentOutputText,
            event.isFinal,
            event.groundingMetadata,
            displayTurnId
          )
          break
        }

        case 'turnComplete':
        case 'generationComplete': {
          // Stale TC for a prior turn (e.g. A's synthesized TC while B is current).
          // Display ids are `${canonical}#n`; compare on the canonical base.
          if (
            event.type === 'turnComplete' &&
            event.turnId &&
            event.source !== 'orchestrator_gate'
          ) {
            const baseOf = (id: string | null | undefined) => {
              if (!id) return null
              const hash = id.lastIndexOf('#')
              return hash > 0 ? id.slice(0, hash) : id
            }
            const currentBases = [
              activeTurnIdsRef.current.model,
              activeTurnIdsRef.current.user,
              baseOf(displayTurnIdRef.current.model),
              baseOf(displayTurnIdRef.current.user),
              baseOf(modelTranscriptSourceRef.current.turnId),
            ].filter((id): id is string => Boolean(id))
            if (currentBases.length > 0 && !currentBases.includes(event.turnId)) {
              liveRuntimeTrace({
                event: 'turn_complete_ignored_stale_turn',
                eventType: event.type,
                runtimeOwner: runtimeOwnershipRef.current.reason,
                sessionPrefix: sessionIdPrefix(sessionId),
                extra: { turnId: event.turnId, currentBases: currentBases.join(',') },
              })
              break
            }
          }
          if (event.type === 'turnComplete' && event.source === 'orchestrator_gate') {
            onOrchestratorGateTurnComplete?.({
              text: gateTurnTextRef.current.trim(),
              ...(event.turnId ? { turnId: event.turnId } : {}),
              ...(event.source ? { source: event.source } : {}),
            })
            gateTurnTextRef.current = ''
            lastGateTurnIdRef.current = null
            transcriptDraftsRef.current.model = ''
            modelTranscriptSourceRef.current = { turnId: null, source: null }
            activeTurnIdsRef.current = { user: null, model: null }
            lastFinalTextRef.current = { user: '', model: '' }
            break
          }
          if (
            event.type === 'turnComplete' &&
            isGateTurnActive?.() &&
            onOrchestratorGateTurnComplete
          ) {
            const fallbackText =
              gateTurnTextRef.current.trim() || transcriptDraftsRef.current.model.trim()
            liveRuntimeTrace({
              event: 'gate_turn_live_fallback',
              route: 'live',
              runtimeOwner: runtimeOwnershipRef.current.reason,
              sessionPrefix: sessionIdPrefix(sessionId),
              textLen: fallbackText.length,
              textPreview: redactTraceText(fallbackText),
              extra: { turnId: event.turnId },
            })
            onOrchestratorGateTurnComplete({
              text: fallbackText,
              ...(event.turnId ? { turnId: event.turnId } : {}),
              source: 'live_model_fallback',
            })
            gateTurnTextRef.current = ''
            lastGateTurnIdRef.current = null
            transcriptDraftsRef.current.model = ''
            modelTranscriptSourceRef.current = { turnId: null, source: null }
            activeTurnIdsRef.current = { user: null, model: null }
            lastFinalTextRef.current = { user: '', model: '' }
            break
          }
          const userDraft = transcriptDraftsRef.current.user.trim()
          const keepUserTurn =
            (event.type === 'turnComplete' && event.source === 'interrupted') ||
            (Boolean(userDraft) && !looksLikeCompletedUtterance(userDraft))

          if (suppressStartupModelOutputRef.current) {
            liveRuntimeTrace({
              event: 'turn_complete_suppressed',
              eventType: event.type,
              suppressionActive: true,
              suppressionReason: startupSuppressionReasonRef.current ?? 'startup',
              runtimeOwner: runtimeOwnershipRef.current.reason,
              sessionPrefix: sessionIdPrefix(sessionId),
            })
            transcriptDraftsRef.current.model = ''
            lastFinalTextRef.current.model = ''
            modelTranscriptSourceRef.current = { turnId: null, source: null }
            activeTurnIdsRef.current.model = null
            if (!keepUserTurn) {
              activeTurnIdsRef.current.user = null
              lastFinalTextRef.current.user = ''
            }
            if (firstConsultingSpokenRef.current) {
              clearStartupModelOutputSuppression('first_consulting_line')
            }
            break
          }

          finalizeOpenModelGeneration()
          if (keepUserTurn) {
            if (!userDraft && lastFinalTextRef.current.user) {
              releaseDisplayTurn('user')
            }
          } else if (userDraft) {
            finalizeOpenUserUtterance()
          } else {
            releaseDisplayTurn('user')
          }
          break
        }

        case 'interrupted':
          finalizeOpenModelGeneration()
          break

        case 'disconnected':
          transcriptDraftsRef.current = { user: '', model: '' }
          lastFinalTextRef.current = { user: '', model: '' }
          activeTurnIdsRef.current = { user: null, model: null }
          resetDisplayTurnIds()
          modelTranscriptSourceRef.current = { turnId: null, source: null }
          startupSuppressionReasonRef.current = null
          suppressStartupModelOutputRef.current = false
          correctionBindSentRef.current = null
          stopFirstConsultingWatch()
          onLiveSessionEnd?.()
          break

        case 'goAway':
          onSignal?.({ action: 'go_away', data: { timeLeft: event.timeLeft } })
          break

        case 'stageUpdate':
          onOrchestratorGateStageUpdate?.({
            ...(event.turnId ? { turnId: event.turnId } : {}),
            ...(event.stage ? { stage: event.stage } : {}),
            ...(event.agent ? { agent: event.agent } : {}),
            ...(event.metadata ? { metadata: event.metadata } : {}),
          })
          break

        case 'signal':
          onSignal?.({
            action: event.action,
            ...(event.turnId ? { turnId: event.turnId } : {}),
            ...(event.data !== undefined ? { data: event.data } : {}),
          })
          break

        default:
          break
      }
    },
  })

  useEffect(() => {
    liveSessionRef.current = liveSession
  }, [liveSession])

  const normalizePolicy = useCallback(
    (policy: LiveSessionPolicy) => ({
      ...policy,
      visualStreamActive: visualStreamActiveRef.current,
    }),
    []
  )

  const waitForSessionReady = useCallback(
    async (timeoutMs = LIVE_READY_TIMEOUT_MS) => {
      if (liveSession.adapter.getIsSessionReady()) {
        return true
      }

      return await new Promise<boolean>(resolve => {
        const timeoutId = setTimeout(() => {
          unsubscribe()
          resolve(false)
        }, timeoutMs)

        const unsubscribe = liveSession.adapter.onEvent(event => {
          if (event.type === 'connected' || event.type === 'sessionReady') {
            clearTimeout(timeoutId)
            unsubscribe()
            resolve(true)
          }

          if (event.type === 'error' || event.type === 'disconnected') {
            clearTimeout(timeoutId)
            unsubscribe()
            resolve(false)
          }
        })
      })
    },
    [liveSession.adapter]
  )

  const sendRealtimeMedia = useCallback(
    (media: { mimeType: string; data: string }): boolean => {
      const mime = media.mimeType?.toLowerCase() ?? ''
      if (mime === 'application/pdf' || mime.includes('pdf') || mime.startsWith('text/') || mime.includes('officedocument')) {
        // PDF/document bytes cannot go on Gemini Live WS. Caller should sendText.
        logger.debug('[useLiveSessionBridge] Skipping document bytes on Live media channel', {
          mimeType: media.mimeType,
        })
        return false
      }
      if (isVisualMimeType(media.mimeType)) {
        const liveTransportReady =
          connectionStateRef.current === LiveConnectionState.CONNECTED &&
          liveSession.adapter.getIsSessionReady()

        if (!liveTransportReady) {
          const now = Date.now()
          if (
            now - skippedVisualFrameLogAtRef.current >
            MEDIA_CAPTURE_CONFIG.VISUAL_FRAME_SKIP_LOG_INTERVAL_MS
          ) {
            skippedVisualFrameLogAtRef.current = now
            logger.debug('[useLiveSessionBridge] Skipping visual frame until Live transport is ready', {
              connectionState: connectionStateRef.current,
              isSessionReady: liveSession.adapter.getIsSessionReady(),
            })
          }
          if (
            connectionStateRef.current === LiveConnectionState.DISCONNECTED &&
            !connectingRef.current
          ) {
            visualStreamActiveRef.current = true
            void handleConnectRef.current({ audio: false })
          }
          return false
        }

        if (!visualStreamActiveRef.current) {
          visualStreamActiveRef.current = true
          liveSession.adapter.sendPolicyUpdate({ visualStreamActive: true })
        }
        markLiveRuntimeOwner('visual_stream')
        liveSession.sendVideoFrame(media as LiveMediaFrame)
        return true
      }

      if (isAudioMimeType(media.mimeType)) {
        liveSession.sendAudioChunk(media)
        return true
      }

      return false
    },
    [liveSession, markLiveRuntimeOwner]
  )

  const setSessionPolicy = useCallback(
    (policy: LiveSessionPolicy) => {
      livePolicyRef.current = policy
      const normalized = normalizePolicy(policy)
      const serialized = JSON.stringify(normalized)
      if (
        connectionStateRef.current !== LiveConnectionState.DISCONNECTED &&
        serialized !== lastSentPolicyJsonRef.current
      ) {
        lastSentPolicyJsonRef.current = serialized
        liveSession.adapter.sendPolicyUpdate(normalized)
      }
      if (typedOutputMuteRef.current) {
        liveSession.setAudioOutputMuted(true)
      } else {
        liveSession.setAudioOutputMuted(policy.responseMode === 'listen_only')
      }
    },
    [liveSession, normalizePolicy]
  )

  const activateAudioInput = useCallback(async () => {
    if (liveSession.isAudioInputActive) return
    // Stop can leave this true after the button is already back on Start.
    audioActivationInFlightRef.current = false

    audioActivationInFlightRef.current = true
    setIsAudioInputStarting(true)
    try {
      typedOutputMuteRef.current = false
      liveSession.setAudioOutputMuted(false)
      await liveSession.startAudioInput()
      setActiveRoute({
        id: GEMINI_MODELS.DEFAULT_VOICE,
        label: 'LIVE UPLINK',
        description: 'Real-time audio/visual stream active.',
        color: ROUTE_COLOR.voice,
      })
      setIsChatVisible(true)
    } finally {
      audioActivationInFlightRef.current = false
      setIsAudioInputStarting(false)
    }
  }, [liveSession, setActiveRoute, setIsChatVisible])

  const handleAudioInputActivationError = useCallback(
    (error: unknown) => {
      logger.error(
        '[useLiveSessionBridge] Failed to start Live audio input',
        error instanceof Error ? error : new Error(String(error))
      )
      const message = error instanceof Error ? error.message : 'Unknown error'
      showToast(
        /permission|microphone|audio/i.test(message)
          ? 'Microphone access failed. Please allow microphone access and try again.'
          : 'Could not start the microphone stream. Text chat and visual sharing remain available.',
        'error'
      )
    },
    [showToast]
  )

  const handleConnect = useCallback(async (opts?: { audio?: boolean }) => {
    const audioRequested = opts?.audio !== false
    const currentConnectionState = connectionStateRef.current
    logger.debug('[handleConnect] START', { audioRequested, currentConnectionState })

    const socketStillOpen =
      currentConnectionState === LiveConnectionState.ERROR &&
      liveSession.adapter.resumeOpenTransport?.() === true
    if (socketStillOpen) {
      connectionStateRef.current = LiveConnectionState.CONNECTED
      setConnectionState(LiveConnectionState.CONNECTED)
    }

    if (
      shouldActivateAudioInputForConnect(
        socketStillOpen ? LiveConnectionState.CONNECTED : currentConnectionState,
        audioRequested,
        liveSession.isAudioInputActive
      )
    ) {
      logger.debug('[handleConnect] audio promotion path — waiting for session ready')
      if (currentConnectionState === LiveConnectionState.CONNECTING) {
        setIsAudioInputStarting(true)
        const ready = await waitForSessionReady()
        if (!ready) {
          setIsAudioInputStarting(false)
          logger.warn('[handleConnect] Live transport was not ready for audio promotion')
          return
        }
      }

      try {
        beginStartupModelOutputSuppression('audio_promotion')
        logger.debug('[handleConnect] activating audio input')
        await activateAudioInput()
        logger.debug('[handleConnect] audio activation complete')
        watchFirstConsultingLine()
      } catch (error) {
        handleAudioInputActivationError(error)
      }
      logger.debug('[handleConnect] audio promotion path — EXIT')
      return
    }

    if (currentConnectionState === LiveConnectionState.CONNECTED) {
      logger.debug('[handleConnect] already connected — EXIT')
      return
    }

    if (currentConnectionState === LiveConnectionState.CONNECTING) {
      logger.debug('[handleConnect] already connecting — waiting for session ready')
      await waitForSessionReady()
      return
    }

    if (connectingRef.current) {
      logger.debug('[handleConnect] connect already in flight — EXIT')
      return
    }

    connectingRef.current = true
    if (audioRequested) {
      setIsAudioInputStarting(true)
    }
    setConnectionState(LiveConnectionState.CONNECTING)
    logger.debug('[handleConnect] audio requested for full connect')

    setActiveRoute({
      id: GEMINI_MODELS.DEFAULT_VOICE,
      label: 'LIVE UPLINK',
      description: 'Real-time audio/visual stream active.',
      color: ROUTE_COLOR.voice,
    })

    try {
      beginStartupModelOutputSuppression('connect')
      const snapshot = clientSessionViewStore.getSnapshot()
      const adoptedSessionId =
        typeof snapshot.sessionId === 'string' ? snapshot.sessionId.trim() : ''
      const languageCode = languageCodeFromSnapshot(snapshot)
      if (livePolicyRef.current.languageCode !== languageCode) {
        livePolicyRef.current = { ...livePolicyRef.current, languageCode }
      }
      await liveSession.connect({
        sessionId: adoptedSessionId || sessionId,
        systemInstruction: '',
        languageCode,
        voiceName: VOICE_CONFIG.DEFAULT_VOICE,
        responseMode: livePolicyRef.current.responseMode,
        affectiveState: livePolicyRef.current.affectiveState,
        visualAttentionEnabled: livePolicyRef.current.visualAttentionEnabled,
        proactiveAudioEnabled: livePolicyRef.current.proactiveAudioEnabled,
        visualStreamActive: visualStreamActiveRef.current,
      })

      if (audioRequested) {
        await activateAudioInput()
      } else {
        liveSession.setAudioOutputMuted(true)
      }

      setIsChatVisible(true)
      watchFirstConsultingLine()
    } catch (error) {
      logger.error(
        '[useLiveSessionBridge] Failed to connect Live session',
        error instanceof Error ? error : new Error(String(error))
      )
      await liveSession.disconnect().catch(() => undefined)
      setConnectionState(LiveConnectionState.ERROR)
      setActiveRoute(createReadyRoute('Live connection failed. Text chat remains available.'))
      const message = error instanceof Error ? error.message : 'Unknown error'
      showToast(
        /permission|microphone|audio/i.test(message)
          ? 'Microphone access failed. Please allow microphone access and try again.'
          : 'Network Error: Could not connect to Gemini Live API. Please check your connection.',
        'error'
      )
    } finally {
      connectingRef.current = false
      if (!audioActivationInFlightRef.current) {
        setIsAudioInputStarting(false)
      }
    }
  }, [
    activateAudioInput,
    beginStartupModelOutputSuppression,
    clientSessionViewStore,
    handleAudioInputActivationError,
    liveSession,
    sessionId,
    setActiveRoute,
    setIsChatVisible,
    showToast,
    waitForSessionReady,
    watchFirstConsultingLine,
  ])

  useEffect(() => {
    handleConnectRef.current = handleConnect
  }, [handleConnect])

  const handleDisconnect = useCallback(async () => {
    connectingRef.current = false
    markLiveRuntimeOwner('none')
    onLiveSessionEnd?.()

    await liveSession.disconnect()
    setConnectionState(LiveConnectionState.DISCONNECTED)
    setActiveRoute(createReadyRoute())
    setIsChatVisible(true)
  }, [
    liveSession,
    markLiveRuntimeOwner,
    onLiveSessionEnd,
    setActiveRoute,
    setIsChatVisible,
  ])

  const handleResumeSession = useCallback(
    async (handle: string) => {
      connectingRef.current = true
      setConnectionState(LiveConnectionState.CONNECTING)

      try {
        await liveSession.resumeSession(handle)
        // Use guarded activateAudioInput instead of direct startAudioInput
        // to prevent race with cleanupAudioInput
        if (!liveSession.isAudioInputActive && !audioActivationInFlightRef.current) {
          await activateAudioInput()
        }
      } catch (err) {
        if (typeof window !== 'undefined') {
          logger.warn('[useLiveSessionBridge] Resume session failed', {
            error: err instanceof Error ? err.message : String(err),
          })
        }
        setConnectionState(LiveConnectionState.ERROR)
      } finally {
        connectingRef.current = false
      }
    },
    [liveSession, activateAudioInput]
  )

  const controller = useMemo<LiveRuntimeController>(
    () => ({
      connect: handleConnect,
      disconnect: handleDisconnect,
      sendText(text, opts) {
        const trimmed = text.trim()
        if (!trimmed) return

        clearStartupModelOutputSuppression('live_send_text')
        markLiveRuntimeOwner('live_turn')
        lastFinalUserPromptRef.current = trimmed
        // v451: a typed line in a voice session must not silence the agent.
        const muteAgent = typedLineMutesAgentAudio({ micActive: liveSession.isAudioInputActive })
        typedOutputMuteRef.current = muteAgent
        if (muteAgent) liveSession.setAudioOutputMuted(true)
        const outbound = composeLiveTypedOutbound(trimmed)
        if (outbound !== trimmed) {
          correctionBindSentRef.current = outbound.slice(trimmed.length).trim()
        }
        liveSession.adapter.sendRealtimeText(outbound)
        if (opts?.recordTranscript !== false) {
          clientSessionViewStore.upsertOptimisticTranscriptItem?.({
            id: generateTranscriptId('user'),
            role: 'user',
            text: trimmed,
            timestamp: new Date(),
            isFinal: true,
          })
        }
      },
      speakAgentOutput(rawText) {
        if (livePolicyRef.current.responseMode === 'listen_only') {
          return
        }

        const cleaned = cleanSpeechText(rawText)
        if (!cleaned) return

        liveRuntimeTrace({
          event: 'speakAgentOutput',
          route: 'live',
          runtimeOwner: runtimeOwnershipRef.current.reason,
          sessionPrefix: sessionIdPrefix(sessionId),
          textLen: cleaned.length,
          textPreview: redactTraceText(cleaned),
        })
        typedOutputMuteRef.current = false
        liveSession.setAudioOutputMuted(false)
        liveSession.adapter.sendRealtimeText(`[SAY] ${cleaned}`)
      },
      sendRealtimeMedia,
      sendContextUpdate(update) {
        if (
          connectionStateRef.current !== LiveConnectionState.CONNECTED &&
          connectionStateRef.current !== LiveConnectionState.CONNECTING
        ) {
          return
        }

        liveSession.adapter.sendContextUpdate({
          ...(update.sessionId ? { sessionId: update.sessionId } : {}),
          modality: update.modality,
          analysis: update.analysis,
          ...(update.imageData ? { imageData: update.imageData } : {}),
          ...(update.capturedAt ? { capturedAt: update.capturedAt } : {}),
          ...(update.metadata ? { metadata: update.metadata } : {}),
        })
      },
      setSessionPolicy,
      setOutputMuted(muted) {
        liveSession.setAudioOutputMuted(muted)
      },
      getConnectionId() {
        return liveSession.adapter.getConnectionId()
      },
      getSessionResumptionHandle() {
        return liveSession.getSessionResumptionHandle()
      },
      getRealtimeTransportState() {
        return {
          isConnected: connectionStateRef.current === LiveConnectionState.CONNECTED,
          isSessionReady: liveSession.adapter.getIsSessionReady(),
          bufferedAmount: liveSession.adapter.getBufferedAmount(),
        }
      },
      getRuntimeOwnershipState() {
        if (connectionStateRef.current === LiveConnectionState.CONNECTED) {
          return { ownsTurns: true, reason: 'connected' }
        }
        if (liveSession.adapter.getIsSessionReady()) {
          return { ownsTurns: true, reason: 'session_ready' }
        }
        return runtimeOwnershipRef.current
      },
      getIsAudioInputActive() {
        return liveSession.isAudioInputActive
      },
      async stopVoiceInput() {
        await liveSession.stopAudioInput()
      },
      stopActiveGeneration() {
        markLiveRuntimeOwner('none')
        liveSession.setAudioOutputMuted(true)
      },
      getIsSessionReady() {
        return liveSession.adapter.getIsSessionReady()
      },
      waitForSessionReady(timeoutMs) {
        return waitForSessionReady(timeoutMs)
      },
      resumeSession(handle) {
        return handleResumeSession(handle)
      },
      pauseHeartbeat() {
        /* heartbeat is owned by the gateway transport */
      },
      resumeHeartbeat() {
        /* heartbeat is owned by the gateway transport */
      },
    }),
    [
      handleConnect,
      handleDisconnect,
      handleResumeSession,
      clearStartupModelOutputSuppression,
      liveSession,
      markLiveRuntimeOwner,
      sendRealtimeMedia,
      setSessionPolicy,
      clientSessionViewStore,
      waitForSessionReady,
    ]
  )

  useEffect(() => {
    liveServiceRef.current = controller
    return () => {
      if (liveServiceRef.current === controller) {
        liveServiceRef.current = null
      }
    }
  }, [controller, liveServiceRef])

  useEffect(() => {
    setSessionPolicy(livePolicy)
  }, [livePolicy, setSessionPolicy])

  useEffect(() => {
    const pushLanguage = () => {
      const languageCode = languageCodeFromSnapshot(uiPreferenceStore.getSnapshot())
      if (livePolicyRef.current.languageCode === languageCode) return
      setSessionPolicy({ ...livePolicyRef.current, languageCode })
    }
    const unsubscribe = uiPreferenceStore.subscribe(pushLanguage)
    return () => {
      unsubscribe()
    }
  }, [setSessionPolicy])

  const handleSendRealtimeInput = useCallback(
    (chunks: Array<{ mimeType: string; data: string }>) => {
      for (const chunk of chunks) {
        controller.sendRealtimeMedia(chunk)
      }
    },
    [controller]
  )

  const handleSendContextUpdate = useCallback(
    (update: VoiceContextUpdate) => {
      if (!update.analysis) return
      controller.sendContextUpdate({
        ...(update.sessionId !== undefined ? { sessionId: update.sessionId } : {}),
        modality: update.modality,
        analysis: update.analysis,
        ...(update.imageData !== undefined ? { imageData: update.imageData } : {}),
        ...(update.capturedAt !== undefined ? { capturedAt: update.capturedAt } : {}),
        ...(update.metadata !== undefined ? { metadata: update.metadata } : {}),
      })
    },
    [controller]
  )

  const handleSendVideoFrame = useCallback(
    (frame: LiveMediaFrame) => {
      return controller.sendRealtimeMedia(frame)
    },
    [controller]
  )

  return {
    liveServiceRef,
    connectionState,
    isAudioInputActive: liveSession.isAudioInputActive,
    isAudioInputStarting,
    setConnectionState,
    handleConnect,
    handleDisconnect,
    handleSendRealtimeInput,
    handleSendVideoFrame,
    handleSendContextUpdate,
    getAudioData: liveSession.getAudioData,
  }
}
